import React, { useEffect, useRef, useState, memo } from 'react';
import {
  Heart, Users, Compass, ArrowRight,
  Activity, Music, Wind,
} from 'lucide-react';
import { ENROLLMENT_URL, openEnrollment } from '../lib/enrollment';

/**
 * ActiveAdults — premium community lifestyle spotlight (v3, 2026-02-22).
 *
 * v3 changes (per user's iter12 brief):
 *   • Embed the YouTube video (Xz-rzvCkjwY) replacing the prior .mov file.
 *     Title / channel / suggested-videos are all suppressed via a
 *     custom poster overlay + YouTube embed params (rel=0,
 *     modestbranding=1, controls=0, iv_load_policy=3, playsinline=1,
 *     disablekb=1). The iframe only mounts AFTER an explicit user tap,
 *     so on every device:
 *       - zero YouTube bytes load on first paint
 *       - no YouTube title / channel ever flashes before user intent
 *   • One-view layout: compact two-column (video LEFT, schedule + CTAs
 *     RIGHT). Headline + emotional copy live above the grid. The whole
 *     section fits comfortably on a 1440×900 screen without scrolling
 *     and on phones in two short scroll regions.
 *   • Three schedule cards are color-coded by class (Senior Fit = red
 *     accent, Zumba Gold = amber accent, Restorative Yoga = rose
 *     accent) while keeping the dark cinematic identity intact.
 *
 * Built in isolation: only this file + the existing scoped CSS
 * keyframes are involved. App.js placement is the only other change.
 */

const SLIDES = [
  { src: '/img/senior-fit-slide.webp',       label: 'Senior Fit' },
  { src: '/img/restorative-yoga-slide.webp', label: 'Restorative Yoga' },
  { src: '/img/zumba-gold-slide.webp',       label: 'Zumba\u00AE Gold' },
];

// Per-class accent palette. Each entry keeps the dark luxury identity
// while giving the card a unique color signature for visual scanning.
const CLASS_CARDS = [
  {
    id: 'senior-fit',
    name: 'Senior Fit',
    Icon: Activity,
    tagline: 'Strength · Stability · Stamina',
    times: [
      { day: 'WED', hour: '10:30 AM' },
      { day: 'SAT', hour: '10:30 AM' },
    ],
    accent: 'red',
    barColor: 'bg-red-500',
    iconBg: 'bg-red-600/15 border-red-500/45',
    iconColor: 'text-red-400',
    timePill: 'text-red-300 border-red-500/45 bg-red-600/15',
    glow: 'group-hover:shadow-[0_20px_50px_rgba(239,43,43,0.30)]',
    hoverBorder: 'hover:border-red-600/60',
  },
  {
    id: 'zumba-gold',
    name: 'Zumba Gold',
    Icon: Music,
    tagline: 'Dance · Joy · Cardio',
    times: [
      { day: 'TUE', hour: '10:30 AM' },
      { day: 'THU', hour: '10:30 AM' },
    ],
    accent: 'amber',
    barColor: 'bg-amber-400',
    iconBg: 'bg-amber-500/15 border-amber-400/45',
    iconColor: 'text-amber-300',
    timePill: 'text-amber-200 border-amber-400/45 bg-amber-500/15',
    glow: 'group-hover:shadow-[0_20px_50px_rgba(245,158,11,0.30)]',
    hoverBorder: 'hover:border-amber-400/60',
  },
  {
    id: 'restorative-yoga',
    name: 'Restorative Yoga',
    Icon: Wind,
    tagline: 'Breath · Balance · Recovery',
    times: [
      { day: 'MON', hour: '10:30 AM' },
      { day: 'FRI', hour: '10:30 AM' },
    ],
    accent: 'rose',
    barColor: 'bg-rose-400',
    iconBg: 'bg-rose-500/15 border-rose-400/45',
    iconColor: 'text-rose-300',
    timePill: 'text-rose-200 border-rose-400/45 bg-rose-500/15',
    glow: 'group-hover:shadow-[0_20px_50px_rgba(244,63,94,0.30)]',
    hoverBorder: 'hover:border-rose-400/60',
  },
];

const TRUST_PILLS = [
  { Icon: Heart,   label: 'MOBILITY' },
  { Icon: Users,   label: 'COMMUNITY' },
  { Icon: Compass, label: 'LOW IMPACT' },
];

/* ============================================================
   ClassSlideshow (replaces former Senior Fit video)
   ----------------------------------------------------------------
   Rotates through 3 static images every 3 seconds with a soft CSS
   opacity fade. Zero new libraries, no <video> element, no
   YouTube/iframe, no autoplay media. Pauses when the slideshow is
   off-screen (IntersectionObserver) so we don't keep firing the
   interval for nothing on long pages.

   Each slide is a self-hosted WebP (~17–24 KB) generated from the
   client-supplied JPEGs and stored under /public/img/. The container
   keeps the exact same outer styling as the prior video frame — same
   aspect-video, same rounded corners, same dark frame — so the
   surrounding section layout doesn't shift.
   ============================================================ */
function LazyYouTubeFrameInner() {
  const wrapRef = useRef(null);
  const [index, setIndex] = useState(0);

  // Rotate every 3 s. Pauses entirely when the slideshow scrolls out
  // of view (IntersectionObserver) — no wasted timers, no battery
  // drain on long mobile sessions. Also paused on tab-hidden so iOS
  // Safari doesn't keep the timer warm in background.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let timer = 0;
    let inView = true;
    let docVisible = !document.hidden;
    const tick = () => { setIndex((i) => (i + 1) % SLIDES.length); };
    const start = () => {
      if (timer) return;
      timer = window.setInterval(tick, 3000);
    };
    const stop = () => {
      if (timer) { window.clearInterval(timer); timer = 0; }
    };
    const recompute = () => {
      if (inView && docVisible) start(); else stop();
    };
    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; recompute(); }, { threshold: 0.15 });
      io.observe(wrap);
    } else {
      start();
    }
    const onVis = () => { docVisible = !document.hidden; recompute(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="active-adults-video"
      className="relative w-full aspect-video rounded-2xl sm:rounded-3xl overflow-hidden bg-zinc-950"
      style={{
        contain: 'layout paint style',
        transform: 'translateZ(0)',
        willChange: 'transform',
      }}
    >
      {/* Slides — all three stacked, only the active one is opacity:1.
          Pure CSS opacity transition is GPU-cheap (no layout, no
          paint). The first slide is eager-loaded (it's above the fold
          in this section); the other two are lazy. */}
      {SLIDES.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.label}
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={i === 0 ? 'high' : 'auto'}
          width={640}
          height={360}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out"
          style={{ opacity: i === index ? 1 : 0 }}
        />
      ))}

      {/* Dark gradient overlay — keeps text legible over any slide. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.0) 38%, rgba(0,0,0,0.0) 62%, rgba(0,0,0,0.65) 100%)' }}
      />

      {/* Bottom caption — animates in via the same opacity transition. */}
      <div className="absolute bottom-0 inset-x-0 z-[12] flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 pointer-events-none">
        <div className="flex flex-col gap-0.5">
          <span className="text-[9px] sm:text-[10px] tracking-[0.45em] text-red-400 font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">
            NOW SHOWING
          </span>
          <span
            data-testid="slideshow-caption"
            key={SLIDES[index].label}
            className="font-display text-lg sm:text-2xl text-white leading-tight"
            style={{ textShadow: '0 4px 18px rgba(0,0,0,0.85)' }}
          >
            {SLIDES[index].label}
          </span>
        </div>
        {/* Slide indicator dots */}
        <div className="flex items-center gap-1.5 pointer-events-auto" role="tablist" aria-label="Slideshow progress">
          {SLIDES.map((s, i) => (
            <span
              key={s.src}
              role="tab"
              aria-selected={i === index}
              aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-6 bg-red-500' : 'w-1.5 bg-white/40'}`}
            />
          ))}
        </div>
      </div>

      {/* Inset red frame glow — keeps the premium FitnessPro feel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl sm:rounded-3xl z-[15]"
        style={{ boxShadow: 'inset 0 0 0 1.5px rgba(239,43,43,0.35), inset 0 0 50px rgba(239,43,43,0.10)' }}
      />
    </div>
  );
}

// React.memo prevents the video from re-rendering when the parent
// ActiveAdults re-renders (e.g., when its parallax CSS-var updates
// the halo positions on every mouse move). Component stability =
// no playback interruption during scroll / hover. The component takes
// no props, so the memoization is always a hit after first mount.
const LazyYouTubeFrame = memo(LazyYouTubeFrameInner);

function ScheduleColorCard({ card, index }) {
  const { Icon } = card;
  return (
    <div
      data-testid={`active-adults-class-${index}`}
      className={`group relative rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950/85 backdrop-blur-sm transition-all duration-400 ${card.hoverBorder} hover:-translate-y-1 ${card.glow}`}
    >
      {/* Color-coded LEFT BORDER stripe — primary scanning signal */}
      <div className={`absolute top-0 bottom-0 left-0 w-1 ${card.barColor}`} />

      {/* On <640px we stack the schedule pills BELOW the name to avoid
          horizontal squeeze (icon + 2 pills + name was overflowing on
          iPhone SE). On ≥640px we keep the original side-by-side row. */}
      <div className="relative pl-4 pr-3 sm:pl-6 sm:pr-5 py-3.5 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          {/* Icon plate (color-tinted) */}
          <span className={`flex-shrink-0 inline-flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-xl border ${card.iconBg} group-hover:scale-110 transition-transform duration-300`}>
            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${card.iconColor}`} />
          </span>

          {/* Name + tagline */}
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-base sm:text-xl text-white leading-none mb-1 truncate">
              {card.name}
            </h3>
            <p className="text-[9px] sm:text-[11px] tracking-[0.16em] sm:tracking-[0.18em] text-zinc-500 font-semibold uppercase truncate">
              {card.tagline}
            </p>
          </div>
        </div>

        {/* Schedule pills — wrap horizontally on mobile (under the
            name), stacked vertically on the right edge ≥640px. */}
        <div className="flex-shrink-0 flex flex-row sm:flex-col flex-wrap gap-1.5 pl-[52px] sm:pl-0">
          {card.times.map((t) => (
            <div
              key={`${card.id}-${t.day}`}
              className={`flex items-center gap-2 px-2.5 py-1 rounded-md border text-[10px] sm:text-[11px] font-bold tracking-[0.14em] sm:tracking-[0.15em] ${card.timePill} whitespace-nowrap`}
            >
              <span className="opacity-70">{t.day}</span>
              <span>{t.hour}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ActiveAdults() {
  const sectionRef = useRef(null);

  // RAF-coalesced parallax on the ambient halos — same pattern About
  // and Trainers use. Skipped on coarse pointer + prefers-reduced-motion.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;
    let rect = el.getBoundingClientRect();
    const cacheRect = () => { rect = el.getBoundingClientRect(); };
    let raf = 0;
    let lx = 0;
    let ly = 0;
    const onMove = (e) => {
      // Suspend parallax while the YouTube iframe is active — every
      // CSS-var update repaints the two big blur halos behind the
      // section, stealing compositor bandwidth from the video decoder.
      // Tested: this single guard removed visible playback judder on
      // 1080p+ streams in iter_13.
      if (document.body.classList.contains('is-video-playing')) return;
      lx = e.clientX;
      ly = e.clientY;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        el.style.setProperty('--aa-mx', `${((lx - rect.left) / rect.width - 0.5) * 12}px`);
        el.style.setProperty('--aa-my', `${((ly - rect.top) / rect.height - 0.5) * 12}px`);
      });
    };
    el.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', cacheRect, { passive: true });
    window.addEventListener('resize', cacheRect, { passive: true });
    return () => {
      el.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', cacheRect);
      window.removeEventListener('resize', cacheRect);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="active-adults"
      ref={sectionRef}
      data-testid="active-adults-section"
      className="relative py-10 sm:py-20 lg:py-24 bg-black overflow-hidden"
      style={{ '--aa-mx': '0px', '--aa-my': '0px' }}
    >
      {/* Ambient red glow halos with parallax — desktop only. On
          coarse-pointer/mobile these two 140-150px blur passes cost
          significant compositor time per frame and contribute to the
          reported video lag. Hidden via Tailwind's `hidden sm:block`
          AND additionally targeted by the @media (pointer: coarse)
          rule in index.css for tablets in landscape. */}
      <div aria-hidden className="aa-mobile-hide pointer-events-none absolute inset-0 hidden sm:block">
        <div
          className="absolute top-12 right-[6%] w-[42%] h-[55%] bg-red-700/12 blur-[140px] rounded-full"
          style={{ transform: 'translate3d(var(--aa-mx), var(--aa-my), 0)' }}
        />
        <div
          className="absolute bottom-0 left-[3%] w-[35%] h-[45%] bg-red-600/10 blur-[150px] rounded-full"
          style={{ transform: 'translate3d(calc(var(--aa-mx) * -0.7), calc(var(--aa-my) * -0.7), 0)' }}
        />
      </div>

      <div className="relative max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* === ONE-VIEW HEADER === */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12 reveal-up">
          {/* Eyebrow heading — scaled up to act as the section's
              primary kicker (per user request "make this heading
              bigger than the second heading"). Tracking dialled back
              from 0.32em to 0.16-0.2em so the larger glyphs read
              tightly instead of stretching across the viewport. */}
          <div className="inline-flex items-center gap-2 sm:gap-3 lg:gap-4 text-base sm:text-2xl lg:text-3xl xl:text-4xl tracking-[0.2em] sm:tracking-[0.18em] lg:tracking-[0.16em] text-red-500 font-bold mb-4 sm:mb-5 lg:mb-6 leading-none">
            <Compass className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 xl:h-7 xl:w-7 flex-shrink-0" />
            <span style={{ textShadow: '0 0 18px rgba(239,43,43,0.45), 0 4px 18px rgba(239,43,43,0.28)' }}>
              ACTIVE SENIOR LIFESTYLE
            </span>
            <Compass className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 xl:h-7 xl:w-7 flex-shrink-0" />
          </div>
          <h2
            className="font-display text-2xl sm:text-5xl lg:text-7xl xl:text-8xl text-white leading-[0.95] tracking-tight mb-4 sm:mb-6 px-2 sm:px-0"
            style={{ textWrap: 'balance' }}
          >
            THE BEST YEARS ARE STILL{' '}
            <span
              className="text-red-500"
              style={{
                textShadow:
                  '0 0 28px rgba(239,43,43,0.45), 0 6px 28px rgba(239,43,43,0.32), 0 2px 0 rgba(0,0,0,0.45)',
              }}
            >
              IN MOTION.
            </span>
          </h2>
          <p className="text-[13px] sm:text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed px-2 sm:px-0">
            Move boldly. Feel strong. Belong to a community built for confidence, mobility, and energy — at every stage of life.
          </p>

          {/* Trust pills row — compact, inline */}
          <div className="mt-4 sm:mt-5 flex flex-wrap justify-center gap-1.5 sm:gap-3">
            {TRUST_PILLS.map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-zinc-950/70 border border-zinc-800 text-[9px] sm:text-[11px] tracking-[0.16em] sm:tracking-[0.2em] text-zinc-200 font-bold"
              >
                <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-red-400" />
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* === TWO-COLUMN ONE-VIEW BODY === */}
        <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 items-stretch">
          {/* LEFT: cinematic YouTube video frame */}
          <div className="lg:col-span-7 reveal-up">
            <LazyYouTubeFrame />
          </div>

          {/* RIGHT: schedule + CTAs */}
          <div className="lg:col-span-5 flex flex-col gap-2.5 sm:gap-4 reveal-up" style={{ animationDelay: '120ms' }}>
            <div className="text-[9px] sm:text-[11px] tracking-[0.32em] sm:tracking-[0.4em] text-red-500 font-bold mb-0.5 sm:mb-1">
              WEEKLY SCHEDULE
            </div>
            {CLASS_CARDS.map((c, i) => (
              <ScheduleColorCard key={c.id} card={c} index={i} />
            ))}

            {/* CTAs side-by-side on desktop, stacked on mobile */}
            <div className="mt-2 sm:mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
              <a
                href={`${ENROLLMENT_URL}&plan=1-year-individual`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openEnrollment({ plan: '1-year-individual' }); }}
                data-cursor="hover"
                data-testid="active-adults-cta-primary"
                className="btn-shimmer group relative inline-flex items-center justify-center gap-2 px-5 py-3 sm:py-3.5 bg-red-600 hover:bg-red-500 text-white text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] font-bold rounded-md transition-colors shadow-[0_10px_30px_rgba(239,43,43,0.35)] hover:shadow-[0_14px_42px_rgba(239,43,43,0.55)]"
              >
                START NOW
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#contact"
                data-cursor="hover"
                data-testid="active-adults-cta-secondary"
                className="group inline-flex items-center justify-center gap-2 px-5 py-3 sm:py-3.5 bg-transparent hover:bg-white/5 text-white text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] font-bold rounded-md border border-zinc-700 hover:border-red-600/65 transition-colors"
              >
                FREE TOUR
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
